import { NoSsr } from "@mui/material";
import { useAtomValue } from "jotai";
import type { NextPage, PageProps } from "next";
import { useRouter } from "next/router";
import qs from "qs";
import { useEffect } from "react";

import { accountAtom } from "shared/lib/account";

import { PageSeo } from "scmp-app/components/meta";
import { PlusMobileScreen } from "scmp-app/components/plus/mobile-screen";
import { PlusPaywallSuspense } from "scmp-app/components/plus/plus-paywall-suspense";
import { trackingAtom } from "scmp-app/components/tracking";
import { rosettaAtom } from "scmp-app/lib/rosetta";
import { page as PlusPage } from "scmp-app/pages/plus/index";
import { plusGlobalCss } from "scmp-app/pages/plus/styles";

import { PlusLoginWall } from "./login-wall";
import { PlusPaywall } from "./paywall";
import { PlusLearnMorePlaceholder } from "./placeholder";
import {
  Container,
  ContentContainer,
  LogoContainer,
  PaywallContainer,
  StyledIconSCMPPlusLogo,
  Wrapper,
} from "./styles";

const LearnMorePage: NextPage<PageProps> = () => {
  const router = useRouter();

  const { isLoggedIn } = useAtomValue(accountAtom);
  const asyncRosettaState = useAtomValue(rosettaAtom);
  const hasScmpPlusAccessRight = asyncRosettaState?.result?.hasScmpPlusAccessRight ?? false;

  const { trackPageView } = useAtomValue(trackingAtom);
  useEffect(() => {
    trackPageView?.();
  }, [trackPageView]);

  useEffect(() => {
    if (!hasScmpPlusAccessRight) return;

    const redirectToPlusHomePage = () => {
      const { pathname: plusPagePathname } = PlusPage.route({});

      const parsedQueryString = qs.parse(window.location.search, {
        ignoreQueryPrefix: true,
      }) as Record<string, string>;

      void router.replace({
        pathname: plusPagePathname,
        query: parsedQueryString,
      });
    };

    redirectToPlusHomePage();
  }, [hasScmpPlusAccessRight, router]);

  return (
    <>
      <PageSeo title="SCMP Plus" />
      <Container>
        <PlusMobileScreen />
        <PlusPaywallSuspense fallback={<PlusLearnMorePlaceholder />}>
          <PaywallContainer>
            <Wrapper>
              <LogoContainer $isLoggedIn={isLoggedIn}>
                <StyledIconSCMPPlusLogo />
              </LogoContainer>
              <ContentContainer>
                {!isLoggedIn && (
                  <NoSsr>
                    <PlusLoginWall />
                  </NoSsr>
                )}
                <PlusPaywall />
              </ContentContainer>
            </Wrapper>
          </PaywallContainer>
        </PlusPaywallSuspense>
      </Container>
    </>
  );
};
LearnMorePage.getInitialProps = () => ({
  animateKey: "plus-learnmore",
  appBarConfiguration: {
    variant: "scmp/plus/learnmore",
  },
  appInitializerConfiguration: {
    application: "plus",
  },
  extraGlobalCss: plusGlobalCss,
});

export default LearnMorePage;
